import React, { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useT } from "@/i18n";
import { toCents } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

/** Asigna un contratista nuevo a la obra, o amplía el monto acordado de uno existente (con motivo). */
export const ContratistaDialog = ({ open, onOpenChange, projectId, contratista, existentes = [] }) => {
  const { t } = useT();
  const qc = useQueryClient();
  const isAmend = Boolean(contratista);
  const [subId, setSubId] = useState("");
  const [monto, setMonto] = useState("");
  const [alcance, setAlcance] = useState("");
  const [motivo, setMotivo] = useState("");

  useEffect(() => {
    if (!open) return;
    setSubId(contratista?.sub_id || "");
    setMonto(contratista ? (contratista.monto_acordado_cents / 100).toFixed(2) : "");
    setAlcance(contratista?.alcance || "");
    setMotivo("");
  }, [open, contratista]);

  const { data: subs } = useQuery({
    queryKey: ["subs-lite"], enabled: open && !isAmend,
    queryFn: async () => (await api.get("/subs", { params: { page_size: 200 } })).data.items,
  });
  const disponibles = (subs || []).filter((s) => !existentes.some((c) => c.sub_id === s.id));

  const mutation = useMutation({
    mutationFn: async () => {
      if (isAmend) {
        return api.patch(`/projects/${projectId}/contratistas/${contratista.sub_id}`, {
          monto_acordado_cents: toCents(monto), motivo,
        });
      }
      return api.post(`/projects/${projectId}/contratistas`, {
        sub_id: subId, monto_acordado_cents: toCents(monto), alcance,
      });
    },
    onSuccess: () => {
      toast.success(t("app.saved"));
      qc.invalidateQueries({ queryKey: ["project-detail", projectId] });
      onOpenChange(false);
    },
    onError: (e) => toast.error(apiError(e, t("app.error"))),
  });

  const blocked = (!isAmend && !subId) || toCents(monto) <= 0 || (isAmend && motivo.trim().length < 5) || mutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="contratista-dialog" className="max-w-md rounded-[10px]">
        <DialogHeader>
          <DialogTitle>{isAmend ? t("projects.detail.amend") : t("projects.detail.add_contractor")}</DialogTitle>
        </DialogHeader>

        {!isAmend && (
          <div>
            <Label className="text-[12.5px]">{t("projects.certifications.sub")} *</Label>
            <Select value={subId} onValueChange={setSubId}>
              <SelectTrigger data-testid="contratista-sub-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {disponibles.map((s) => <SelectItem key={s.id} value={s.id}>{s.nombre}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        <div>
          <Label className="text-[12.5px]">{t("projects.detail.agreed_amount")} *</Label>
          <Input data-testid="contratista-amount-input" value={monto} placeholder="0.00" onChange={(e) => setMonto(e.target.value)} />
        </div>
        {!isAmend && (
          <div>
            <Label className="text-[12.5px]">{t("projects.detail.scope")}</Label>
            <Textarea data-testid="contratista-scope-input" rows={2} value={alcance} onChange={(e) => setAlcance(e.target.value)} />
          </div>
        )}
        {isAmend && (
          <div>
            <Label className="text-[12.5px]">{t("projects.detail.amend_reason")} *</Label>
            <Textarea data-testid="contratista-amend-reason-input" rows={2} value={motivo} onChange={(e) => setMotivo(e.target.value)} />
          </div>
        )}

        <DialogFooter>
          <Button data-testid="contratista-submit-button" disabled={blocked} onClick={() => mutation.mutate()}>
            {mutation.isPending && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
            {t("app.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
